import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  Edit, 
  Save, 
  X, 
  Star,
  AlertTriangle,
  Clock,
  RefreshCw,
  Filter,
  Download,
  Upload,
  Eye,
  EyeOff,
  Globe,
  FileText
} from 'lucide-react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import { useAuth } from '../../contexts/AuthContext';

interface MenuItem {
  id: string;
  name: string;
  category: string;
  subcategory?: string;
  price: number;
  description: string;
  ingredients: string[];
  allergens: string[];
  day_part: string;
  availability_hours?: string;
  dietary_restrictions: string[];
  confidence_score: number;
  review_status: 'pending' | 'approved' | 'rejected' | 'needs_review';
  review_notes?: string;
  created_at: string;
}

interface JobDetails {
  id: string;
  enterprise_id: string;
  job_type: string;
  source_url?: string;
  document_path?: string;
  status: string;
  extracted_data?: any;
  created_at: string;
  updated_at: string;
}

interface MenuReviewProps {
  jobId: string;
  onBack?: () => void;
  onComplete?: () => void;
}

const MenuReview: React.FC<MenuReviewProps> = ({ jobId, onBack, onComplete }) => {
  const { getIdToken } = useAuth();
  const [job, setJob] = useState<JobDetails | null>(null);
  const [items, setItems] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [editedItems, setEditedItems] = useState<{ [key: string]: Partial<MenuItem> }>({});
  const [committing, setCommitting] = useState(false);

  const authHeaders = async (): Promise<HeadersInit> => {
    const token = await getIdToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchJobDetails();
    fetchMenuItems();
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.menuSetupJob(jobId), { headers: await authHeaders() });
      if (!response.ok) throw new Error('Failed to fetch job details');
      const payload = await response.json();
      setJob(payload.data || payload);
    } catch (err) {
      console.error('Error fetching job details:', err);
      setError('Failed to load job details');
    }
  };

  const fetchMenuItems = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINTS.menuSetupJobItems(jobId), { headers: await authHeaders() });
      if (!response.ok) throw new Error('Failed to fetch menu items');
      const payload = await response.json();
      const rawItems: any[] = payload?.data?.items || payload?.items || [];
      // Map backend's staged items to this component's MenuItem shape.
      const mapped: MenuItem[] = rawItems.map((it: any) => ({
        id: it.stagingId || it.id,
        name: it.name || '',
        category: it.category || '',
        subcategory: it.subcategory || undefined,
        price: typeof it.price === 'number' ? it.price : (it.price == null ? 0 : Number(it.price) || 0),
        description: it.description || '',
        ingredients: Array.isArray(it.ingredients) ? it.ingredients : [],
        allergens: Array.isArray(it.allergens) ? it.allergens : [],
        day_part: it.day_part || 'all-day',
        availability_hours: it.availability_hours,
        dietary_restrictions: Array.isArray(it.dietary_restrictions) ? it.dietary_restrictions : [],
        confidence_score: typeof it.confidence_score === 'number' ? it.confidence_score : 1,
        review_status: it.review_status || 'pending',
        review_notes: it.review_notes,
        created_at: it.created_at || new Date().toISOString()
      }));
      setItems(mapped);
    } catch (err) {
      console.error('Error fetching menu items:', err);
      setError('Failed to load menu items');
    } finally {
      setLoading(false);
    }
  };

  const commitApprovedItems = async (stagingIds: string[]): Promise<boolean> => {
    if (stagingIds.length === 0) return true;
    setCommitting(true);
    try {
      const overrides: Record<string, any> = {};
      for (const sid of stagingIds) {
        const edits = editedItems[sid];
        if (edits) overrides[sid] = edits;
      }
      const response = await fetch(API_ENDPOINTS.menuSetupApproveJob(jobId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await authHeaders())
        },
        body: JSON.stringify({ stagingIds, overrides })
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.message || payload?.error || `Approve failed (${response.status})`);
      }
      return true;
    } catch (err) {
      console.error('Error committing items:', err);
      setError(err instanceof Error ? err.message : 'Failed to commit items to menu');
      return false;
    } finally {
      setCommitting(false);
    }
  };

  const handleItemEdit = (itemId: string, field: string, value: any) => {
    setEditedItems(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [field]: value
      }
    }));
  };

  const saveItemEdit = async (itemId: string) => {
    const edits = editedItems[itemId];
    if (!edits) return;
    // Edits are held locally until approve() fires; the backend accepts
    // per-stagingId overrides at commit time.
    setItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, ...edits } : item
    ));
    setEditingItem(null);
  };

  const updateItemStatus = async (itemIds: string[], status: string) => {
    if (status === 'approved') {
      const ok = await commitApprovedItems(itemIds);
      if (!ok) return;
      setItems(prev => prev.map(item =>
        itemIds.includes(item.id) ? { ...item, review_status: 'approved' as any } : item
      ));
      onComplete?.();
      return;
    }
    // Rejection / needs_review are local-only flags; the backend just
    // leaves them as staged until approve() commits the approved subset.
    setItems(prev => prev.map(item =>
      itemIds.includes(item.id) ? { ...item, review_status: status as any } : item
    ));
  };

  const approveAllItems = async () => {
    const pendingItems = items
      .filter(item => item.review_status === 'pending')
      .map(item => item.id);

    if (pendingItems.length > 0) {
      await updateItemStatus(pendingItems, 'approved');
    }
  };

  const toggleItemSelection = (itemId: string) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const getConfidenceColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600 bg-green-100';
    if (score >= 0.6) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      case 'needs_review': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const filteredItems = items.filter(item => {
    const matchesStatus = filterStatus === 'all' || item.review_status === filterStatus;
    const matchesCategory = filterCategory === 'all' || item.category === filterCategory;
    const matchesSearch = searchTerm === '' || 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesStatus && matchesCategory && matchesSearch;
  });

  const categories = Array.from(new Set(items.map(item => item.category)));
  const stats = {
    total: items.length,
    approved: items.filter(item => item.review_status === 'approved').length,
    rejected: items.filter(item => item.review_status === 'rejected').length,
    pending: items.filter(item => item.review_status === 'pending').length,
    needsReview: items.filter(item => item.review_status === 'needs_review').length
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-3"></div>
          <span className="text-gray-600">Loading extracted menu items...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">Error: {error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              {job?.job_type === 'website' ? (
                <Globe className="w-6 h-6 text-blue-600" />
              ) : (
                <FileText className="w-6 h-6 text-blue-600" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">
                Review Extracted Menu Items
              </h1>
              <p className="text-gray-600">
                {job?.job_type === 'website' ? (
                  <>Source: <span className="font-medium">{job?.source_url}</span></>
                ) : (
                  <>Document: <span className="font-medium">{job?.document_path}</span></>
                )}
              </p>
              <p className="text-sm text-gray-500">
                Job ID: {jobId} • Extracted on {job ? new Date(job.created_at).toLocaleDateString() : ''}
              </p>
            </div>
          </div>
          <div className="flex space-x-3">
            {onBack && (
              <button
                onClick={onBack}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Back to Tools
              </button>
            )}
            <button
              onClick={approveAllItems}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors flex items-center"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve All Pending
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 rounded-lg">
          <div className="text-2xl font-bold">{stats.total}</div>
          <div className="text-blue-100 text-sm">Total Items</div>
        </div>
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4 rounded-lg">
          <div className="text-2xl font-bold">{stats.approved}</div>
          <div className="text-green-100 text-sm">Approved</div>
        </div>
        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white p-4 rounded-lg">
          <div className="text-2xl font-bold">{stats.pending}</div>
          <div className="text-yellow-100 text-sm">Pending</div>
        </div>
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-lg">
          <div className="text-2xl font-bold">{stats.needsReview}</div>
          <div className="text-orange-100 text-sm">Needs Review</div>
        </div>
        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-4 rounded-lg">
          <div className="text-2xl font-bold">{stats.rejected}</div>
          <div className="text-red-100 text-sm">Rejected</div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>
          
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="needs_review">Needs Review</option>
          </select>
          
          <select 
            value={filterCategory} 
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm"
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
          
          <input
            type="text"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm flex-1 min-w-48"
          />
          
          {selectedItems.size > 0 && (
            <div className="flex items-center space-x-2 ml-auto">
              <span className="text-sm text-gray-600">{selectedItems.size} selected</span>
              <button
                onClick={() => updateItemStatus(Array.from(selectedItems), 'approved')}
                className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white text-sm rounded-md transition-colors"
              >
                Approve Selected
              </button>
              <button
                onClick={() => updateItemStatus(Array.from(selectedItems), 'rejected')}
                className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm rounded-md transition-colors"
              >
                Reject Selected
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Menu Items */}
      <div className="space-y-4">
        {filteredItems.length === 0 ? (
          <div className="bg-white rounded-lg border p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No items found</h3>
            <p className="text-gray-500">
              {items.length === 0 
                ? "No menu items were extracted from this source."
                : "No items match your current filters."
              }
            </p>
          </div>
        ) : (
          filteredItems.map((item) => (
            <div
              key={item.id}
              className={`bg-white rounded-lg border p-6 transition-all ${
                selectedItems.has(item.id) ? 'ring-2 ring-blue-500 border-blue-300' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  <input
                    type="checkbox"
                    checked={selectedItems.has(item.id)}
                    onChange={() => toggleItemSelection(item.id)}
                    className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300"
                  />
                  
                  <div className="flex-1">
                    {editingItem === item.id ? (
                      <div className="space-y-4">
                        <input
                          type="text"
                          value={editedItems[item.id]?.name || item.name}
                          onChange={(e) => handleItemEdit(item.id, 'name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md font-semibold text-lg"
                          placeholder="Item name"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                            <input
                              type="text"
                              value={editedItems[item.id]?.category || item.category}
                              onChange={(e) => handleItemEdit(item.id, 'category', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Price</label>
                            <input
                              type="number"
                              step="0.01"
                              value={editedItems[item.id]?.price || item.price}
                              onChange={(e) => handleItemEdit(item.id, 'price', parseFloat(e.target.value))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                          <textarea
                            value={editedItems[item.id]?.description || item.description}
                            onChange={(e) => handleItemEdit(item.id, 'description', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            rows={3}
                          />
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
                          <span className="text-lg font-bold text-green-600">
                            ${item.price?.toFixed(2) || 'N/A'}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(item.review_status)}`}>
                            {item.review_status.replace('_', ' ')}
                          </span>
                          <span className={`px-2 py-1 text-xs rounded-full ${getConfidenceColor(item.confidence_score)}`}>
                            {Math.round(item.confidence_score * 100)}% confidence
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                          <div>
                            <span className="text-sm font-medium text-gray-700">Category:</span>
                            <span className="ml-2 text-sm text-gray-600">{item.category}</span>
                          </div>
                          {item.day_part && (
                            <div>
                              <span className="text-sm font-medium text-gray-700">Day Part:</span>
                              <span className="ml-2 text-sm text-gray-600">{item.day_part}</span>
                            </div>
                          )}
                        </div>
                        
                        {item.description && (
                          <p className="text-gray-600 mb-3">{item.description}</p>
                        )}
                        
                        {item.ingredients && item.ingredients.length > 0 && (
                          <div className="mb-3">
                            <span className="text-sm font-medium text-gray-700">Ingredients:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {item.ingredients.map((ingredient, idx) => (
                                <span key={idx} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                  {ingredient}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {item.dietary_restrictions && item.dietary_restrictions.length > 0 && (
                          <div className="mb-3">
                            <span className="text-sm font-medium text-gray-700">Dietary:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {item.dietary_restrictions.map((restriction, idx) => (
                                <span key={idx} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                                  {restriction}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 ml-4">
                  {editingItem === item.id ? (
                    <>
                      <button
                        onClick={() => saveItemEdit(item.id)}
                        className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                        title="Save changes"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingItem(null);
                          setEditedItems(prev => {
                            const { [item.id]: _, ...rest } = prev;
                            return rest;
                          });
                        }}
                        className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors"
                        title="Cancel editing"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setEditingItem(item.id)}
                        className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 rounded-lg transition-colors"
                        title="Edit item"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => updateItemStatus([item.id], 'approved')}
                        className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                        title="Approve item"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => updateItemStatus([item.id], 'rejected')}
                        className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                        title="Reject item"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Action Bar */}
      {items.length > 0 && (
        <div className="sticky bottom-6 mt-8">
          <div className="bg-white rounded-lg shadow-lg border p-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {filteredItems.length} of {items.length} items • {stats.approved} approved • {stats.pending} pending
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => console.log('Export approved items')}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </button>
                <button
                  onClick={() => console.log('Deploy to menu')}
                  className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Deploy to Menu
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuReview;